/*
 * Company/store backend router.
 *
 * Modes:
 *  - online_only: Firebase/online backend only.
 *  - offline_server_only: local store gateway is authoritative for the UI.
 *  - hybrid: local-first, with explicit cloud fallback when the gateway is down.
 *
 * The configuration is persisted on the tenant under operationalSettings.offline
 * and cached in localStorage so the router is available before Settings is opened.
 */

export const SERVER_MODES = Object.freeze({
  ONLINE_ONLY: 'online_only',
  OFFLINE_SERVER_ONLY: 'offline_server_only',
  HYBRID: 'hybrid',
});

export const DEFAULT_SERVER_CONFIG = Object.freeze({
  serverMode: SERVER_MODES.ONLINE_ONLY,
  localServer: {
    enabled: false,
    url: '',
    storeId: '',
    serverId: '',
    token: '',
    timeoutMs: 3000,
    autoDiscovery: false,
  },
  cloud: {
    enabled: true,
    allowFallback: true,
  },
  hybrid: {
    preferredSource: 'local',
    switchBackAutomatically: true,
  },
  sync: {
    enabled: true,
    automatic: true,
    intervalSeconds: 30,
    syncFiles: true,
    backgroundSync: true,
  },
});

const CONFIG_KEY = 'coldstorage.infrastructure.v1';
const STATUS_EVENT = 'coldstorage:infrastructure-status';
const nowIso = () => new Date().toISOString();
const hasWindow = () => typeof window !== 'undefined';

const safeJson = (value) => {
  if (value == null) return value;
  if (Array.isArray(value)) return value.map(safeJson);
  if (value instanceof Date) return value.toISOString();
  if (typeof value?.toDate === 'function') {
    try { return value.toDate().toISOString(); } catch (_) {}
  }
  if (typeof value === 'object') {
    // Firebase DocumentReference / GeoPoint / Timestamp compatibility.
    if (typeof value.path === 'string' && value.path) return { __firestoreRef: value.path };
    if (typeof value.latitude === 'number' && typeof value.longitude === 'number') {
      return { latitude: value.latitude, longitude: value.longitude };
    }
    const result = {};
    Object.entries(value).forEach(([key, current]) => {
      if (typeof current !== 'function' && current !== undefined) result[key] = safeJson(current);
    });
    return result;
  }
  return value;
};

const mergeConfig = (value = {}) => ({
  ...DEFAULT_SERVER_CONFIG,
  ...(value || {}),
  localServer: { ...DEFAULT_SERVER_CONFIG.localServer, ...(value?.localServer || {}) },
  cloud: { ...DEFAULT_SERVER_CONFIG.cloud, ...(value?.cloud || {}) },
  hybrid: { ...DEFAULT_SERVER_CONFIG.hybrid, ...(value?.hybrid || {}) },
  sync: { ...DEFAULT_SERVER_CONFIG.sync, ...(value?.sync || {}) },
});

const normalizeBaseUrl = (value = '') => String(value || '').trim().replace(/\/+$/, '');

const PLACEHOLDER_LOCAL_URL = 'http://192.168.1.10:8080';
const LOCAL_FAILURE_BASE_COOLDOWN_MS = 15000;
const LOCAL_FAILURE_MAX_COOLDOWN_MS = 60000;

const isLocalConfiguredValue = (config = {}) => {
  const local = config.localServer || {};
  const base = normalizeBaseUrl(local.url);
  if (local.enabled === false || !base) return false;
  const hasIdentity = Boolean(
    String(local.storeId || '').trim() ||
    String(local.serverId || '').trim() ||
    String(local.token || '').trim()
  );
  // Old builds populated the LAN placeholder for every tenant. Treat that
  // untouched placeholder as "not configured" so existing online tenants do
  // not hammer 192.168.1.10 and fail every Firestore read.
  if (base === PLACEHOLDER_LOCAL_URL && !hasIdentity) return false;
  return true;
};

let lastLocalTestAt = 0;
let localTestInFlight = null;
let localUnavailableUntil = 0;
let consecutiveLocalFailures = 0;

let state = {
  config: mergeConfig(),
  tenantId: '',
  local: { online: false, checkedAt: '', latencyMs: null, error: '' },
  cloud: { online: typeof navigator === 'undefined' ? true : navigator.onLine !== false },
  activeSource: 'cloud',
  lastError: '',
};

const loadCached = () => {
  if (!hasWindow()) return;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CONFIG_KEY) || '{}');
    state = {
      ...state,
      tenantId: String(parsed.tenantId || state.tenantId || ''),
      config: mergeConfig(parsed.config || parsed || {}),
    };
  } catch (_) {}
};
loadCached();

const configuredMode = () => state.config.serverMode || SERVER_MODES.ONLINE_ONLY;
const isLocalConfigured = () => isLocalConfiguredValue(state.config);
const circuitOpen = () =>
  configuredMode() === SERVER_MODES.HYBRID &&
  localUnavailableUntil > Date.now();

const effectiveMode = () => {
  const current = configuredMode();
  if (current === SERVER_MODES.HYBRID && !isLocalConfigured()) {
    return SERVER_MODES.ONLINE_ONLY;
  }
  return current;
};

const snapshot = () => ({
  ...state,
  config: mergeConfig(state.config),
  configuredMode: configuredMode(),
  effectiveMode: effectiveMode(),
  localConfigured: isLocalConfigured(),
  localCircuitOpen: circuitOpen(),
  localUnavailableUntil: localUnavailableUntil || 0,
  localRoutingEnabled:
    (configuredMode() === SERVER_MODES.OFFLINE_SERVER_ONLY ||
      (configuredMode() === SERVER_MODES.HYBRID && isLocalConfigured())) &&
    !circuitOpen(),
});

const emit = () => {
  if (!hasWindow()) return;
  window.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail: snapshot() }));
};

const cache = () => {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(CONFIG_KEY, JSON.stringify({ tenantId: state.tenantId, config: state.config }));
  } catch (_) {}
};

const abortableFetch = async (url, options = {}, timeoutMs = 3000) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), Math.max(500, Number(timeoutMs) || 3000)) : null;
  try {
    return await fetch(url, { ...options, ...(controller ? { signal: controller.signal } : {}) });
  } finally {
    if (timer) clearTimeout(timer);
  }
};

const authHeaders = () => {
  const local = state.config.localServer || {};
  return {
    'Content-Type': 'application/json',
    'X-Store-Id': String(local.storeId || ''),
    'X-Server-Id': String(local.serverId || ''),
    ...(local.token ? { Authorization: `Bearer ${local.token}` } : {}),
    ...(state.tenantId ? { 'X-Tenant-Id': state.tenantId } : {}),
  };
};

const localRequest = async (path, options = {}) => {
  const local = state.config.localServer || {};
  const base = normalizeBaseUrl(local.url);
  const { forceLocalProbe = false, ...fetchOptions } = options || {};
  if (!local.enabled || !base) throw new Error('Local store server is not configured.');

  if (
    configuredMode() === SERVER_MODES.HYBRID &&
    !forceLocalProbe &&
    circuitOpen()
  ) {
    const error = new Error('Local store server temporarily unavailable; using cloud fallback.');
    error.code = 'local-circuit-open';
    throw error;
  }

  const started = Date.now();
  try {
    const response = await abortableFetch(`${base}${path}`, {
      ...fetchOptions,
      headers: { ...authHeaders(), ...(fetchOptions.headers || {}) },
    }, local.timeoutMs);
    const text = await response.text();
    let body = null;
    try { body = text ? JSON.parse(text) : null; } catch (_) { body = { message: text }; }
    if (!response.ok) throw new Error(body?.message || body?.error || `Local server returned HTTP ${response.status}`);

    consecutiveLocalFailures = 0;
    localUnavailableUntil = 0;
    state.local = { online: true, checkedAt: nowIso(), latencyMs: Date.now() - started, error: '' };
    state.lastError = '';
    emit();
    return body;
  } catch (error) {
    consecutiveLocalFailures += 1;
    const cooldown = Math.min(
      LOCAL_FAILURE_MAX_COOLDOWN_MS,
      LOCAL_FAILURE_BASE_COOLDOWN_MS * Math.max(1, consecutiveLocalFailures)
    );
    if (configuredMode() === SERVER_MODES.HYBRID) {
      localUnavailableUntil = Date.now() + cooldown;
    }
    state.local = { online: false, checkedAt: nowIso(), latencyMs: null, error: String(error?.message || error) };
    state.lastError = state.local.error;
    emit();
    throw error;
  }
};

const mode = () => configuredMode();
const localFirst = () =>
  mode() === SERVER_MODES.OFFLINE_SERVER_ONLY ||
  (mode() === SERVER_MODES.HYBRID && isLocalConfigured());
// Hybrid reads always have a safe cloud fallback when cloud access is enabled.
// This keeps branding/settings/labels/navigation readable even when a local
// gateway is down. Write fallback remains separately controllable to avoid
// unintended split-brain operational writes.
const canCloudReadFallback = () =>
  mode() === SERVER_MODES.HYBRID &&
  state.config.cloud?.enabled !== false;
const canCloudFallback = () =>
  canCloudReadFallback() &&
  state.config.cloud?.allowFallback !== false;
const shouldSkipLocalReadForCloud = () =>
  canCloudReadFallback() &&
  (!isLocalConfigured() || circuitOpen());
const shouldSkipLocalWriteForCloud = () =>
  canCloudFallback() &&
  (!isLocalConfigured() || circuitOpen());

const service = {
  EVENT: STATUS_EVENT,
  SERVER_MODES,
  DEFAULT_SERVER_CONFIG,

  getSnapshot: snapshot,
  isLocalConfigured,
  canCloudFallback,
  canCloudReadFallback,
  shouldUseLocal() {
    return localFirst() && !circuitOpen();
  },

  subscribe(listener) {
    if (!hasWindow()) return () => {};
    const handler = (event) => listener(event?.detail || snapshot());
    const online = () => {
      state.cloud.online = true;
      emit();
      if (localFirst()) this.testLocal({ silent: true });
    };
    const offline = () => { state.cloud.online = false; emit(); };
    window.addEventListener(STATUS_EVENT, handler);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    listener(snapshot());
    return () => {
      window.removeEventListener(STATUS_EVENT, handler);
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  },

  setTenantContext(tenantId, offlineConfig = {}) {
    state.tenantId = String(tenantId || state.tenantId || '');
    state.config = mergeConfig(offlineConfig);
    state.cloud.online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
    cache();
    emit();
    return snapshot();
  },

  setConfig(config = {}) {
    state.config = mergeConfig(config);
    cache();
    emit();
    return snapshot();
  },

  async testLocal({ silent = false, force = false } = {}) {
    if (mode() === SERVER_MODES.ONLINE_ONLY || (mode() === SERVER_MODES.HYBRID && !isLocalConfigured())) {
      state.activeSource = 'cloud';
      state.local = {
        ...state.local,
        online: false,
        checkedAt: state.local.checkedAt || '',
        error: mode() === SERVER_MODES.HYBRID ? 'Local store server is not configured.' : '',
      };
      emit();
      return snapshot();
    }
    if (!force && lastLocalTestAt && Date.now() - lastLocalTestAt < 15000) {
      return snapshot();
    }
    if (!force && localTestInFlight) return localTestInFlight;

    const run = (async () => {
      try {
        const health = await localRequest('/health', { method: 'GET', forceLocalProbe: Boolean(force) });
        state.activeSource = 'local';
        state.local = { ...state.local, online: true, details: health || null };
      } catch (error) {
        state.activeSource = canCloudReadFallback() ? 'cloud' : 'offline';
        if (!silent) throw error;
      } finally {
        lastLocalTestAt = Date.now();
        emit();
      }
      return snapshot();
    })();

    localTestInFlight = run;
    try {
      return await run;
    } finally {
      localTestInFlight = null;
    }
  },

  async getLocalStatus() {
    try {
      const body = await localRequest('/v1/status', { method: 'GET' });
      return body || {};
    } catch (error) {
      if (mode() === SERVER_MODES.OFFLINE_SERVER_ONLY) throw error;
      return null;
    }
  },

  async syncNow() {
    return localRequest('/v1/sync/run', { method: 'POST', body: '{}' });
  },

  async retryFailed() {
    return localRequest('/v1/sync/retry', { method: 'POST', body: '{}' });
  },

  async writeDocument({ path, action = 'set', data = null, merge = false, operationId = '' }) {
    if (!localFirst() || shouldSkipLocalWriteForCloud()) return { handled: false, source: 'cloud' };
    try {
      const body = await localRequest('/v1/firestore/write', {
        method: 'POST',
        body: JSON.stringify({
          operationId: operationId || undefined,
          tenantId: state.tenantId,
          storeId: state.config.localServer?.storeId || '',
          path,
          action,
          merge: Boolean(merge),
          data: safeJson(data),
          client: 'react',
          createdAt: nowIso(),
        }),
      });
      state.activeSource = 'local';
      emit();
      return { handled: true, source: 'local', body };
    } catch (error) {
      if (canCloudFallback()) {
        state.activeSource = 'cloud';
        emit();
        return { handled: false, source: 'cloud', fallbackReason: String(error?.message || error) };
      }
      state.activeSource = 'offline';
      emit();
      throw error;
    }
  },

  async batchWrite(operations = []) {
    if (!localFirst() || shouldSkipLocalWriteForCloud()) return { handled: false, source: 'cloud' };
    try {
      const body = await localRequest('/v1/firestore/batch', {
        method: 'POST',
        body: JSON.stringify({
          tenantId: state.tenantId,
          storeId: state.config.localServer?.storeId || '',
          operations: operations.map((op) => ({ ...op, data: safeJson(op.data) })),
          client: 'react',
        }),
      });
      state.activeSource = 'local';
      emit();
      return { handled: true, source: 'local', body };
    } catch (error) {
      if (canCloudFallback()) return { handled: false, source: 'cloud', fallbackReason: String(error?.message || error) };
      throw error;
    }
  },

  async readDocument(path) {
    if (!localFirst() || shouldSkipLocalReadForCloud()) return { handled: false, source: 'cloud' };
    try {
      const body = await localRequest(`/v1/firestore/document?path=${encodeURIComponent(path)}`, { method: 'GET' });
      state.activeSource = 'local';
      return { handled: true, found: body?.found !== false, data: body?.data || null, source: 'local' };
    } catch (error) {
      if (canCloudReadFallback()) return { handled: false, source: 'cloud', fallbackReason: String(error?.message || error) };
      throw error;
    }
  },

  async queryDocuments(spec = {}) {
    if (!localFirst() || shouldSkipLocalReadForCloud()) return { handled: false, source: 'cloud' };
    try {
      const body = await localRequest('/v1/firestore/query', {
        method: 'POST',
        body: JSON.stringify({ ...safeJson(spec), tenantId: state.tenantId }),
      });
      state.activeSource = 'local';
      return { handled: true, rows: Array.isArray(body?.rows) ? body.rows : [], source: 'local' };
    } catch (error) {
      if (canCloudReadFallback()) return { handled: false, source: 'cloud', fallbackReason: String(error?.message || error) };
      throw error;
    }
  },
};

export { safeJson, mergeConfig };
export default service;
